import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, unref as _unref, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, createVNode as _createVNode, createBlock as _createBlock, renderSlot as _renderSlot, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "qtl-formulas"
}
const _hoisted_2 = ["data-col-id"]
const _hoisted_3 = {
  key: 0,
  class: "qtl-tbody"
}
const _hoisted_4 = {
  key: 1,
  class: "qtl-tbody"
}
const _hoisted_5 = {
  key: 0,
  class: "qtl-vpad",
  "aria-hidden": "true"
}
const _hoisted_6 = ["colspan"]
const _hoisted_7 = {
  key: 1,
  class: "qtl-vpad",
  "aria-hidden": "true"
}
const _hoisted_8 = ["colspan"]
const _hoisted_9 = {
  key: 1,
  class: "qtl-empty"
}

import { computed, ref, type CSSProperties } from "vue";
import type { VirtualItem } from "@tanstack/vue-virtual";
import QTLiteHeader from "./QTLiteHeader.vue";
import QTLiteRow from "./QTLiteRow.vue";
import { isColumnDragLayoutAuto } from "../composables/resizeState";
import type { LiteColumn, LiteRow } from "../types";
import type { TableFormulaResult } from "@/models/interfaces/entity-settings/querytable/querytable";
import { formatNumber } from "../cell-items-lite/cellFormat";


export default /*@__PURE__*/_defineComponent({
  __name: 'QTLiteBody',
  props: {
    virtualized: { type: Boolean, default: false },
    virtualRows: { default: () => [] },
    totalSize: { default: 0 },
    scrollMargin: { default: 0 },
    measureElement: { type: Function, default: undefined },
    rows: {},
    measureSampleSize: { default: null },
    columns: {},
    headerMode: { default: "sticky" },
    userWidths: {},
    frozenColWidths: { default: null },
    dragLiveWidths: {},
    widthsLocked: { type: Boolean },
    lockedWidthPx: {},
    effectiveMultiplier: {},
    rowHeightAlignStamp: {},
    srcElement: {},
    WA: { type: Boolean },
    isRowChecked: {},
    rowNumberBase: {},
    formulaResults: {},
    scrollLocked: { type: Boolean },
    loading: { type: Boolean },
    emptyMessage: {},
    contentMode: { default: "normal" },
    headerCheckbox: {},
    multieditAllowed: { type: Boolean },
    orderByPinned: { type: Boolean },
    showGlobalPinnedSort: { type: Boolean }
  },
  emits: ["toggleCheckbox", "changeHoverRecord", "changeHeights", "refreshNeeded", "updatePerson", "updateSelection", "checkBoxClick", "sourceItemsRequested", "headerCheckboxChange", "sort", "multieditClick", "togglePinnedOrder"],
  setup(__props: any, { expose: __expose }) {

const props = __props;

/* Виртуализируем только когда ширины уже зафиксированы (fixed-layout). Пока идёт
   фаза замера ширин по сэмплу, рисуем плоский tbody с сэмплом — виртуализатору
   нужны стабильные ширины, иначе окно строк снова влияло бы на раскладку. */
const useVirtual = computed<boolean>(() => !!props.virtualized && !!props.widthsLocked);

/* В плоском пути (фаза замера или виртуализация выключена) рисуем сэмпл или все строки.
   Это единственное место, где решается, сколько строк реально попадёт в DOM. */
const renderedRows = computed<LiteRow[]>(() => {
  const n = props.measureSampleSize;
  if (n != null && n >= 0 && n < props.rows.length) return props.rows.slice(0, n);
  return props.rows;
});

const colCount = computed<number>(() => props.columns.length);

/* Высота верхней/нижней распорки: переводим абсолютные (с учётом scrollMargin)
   офсеты виртуальных строк в координаты внутри tbody. Сумма распорок и высот
   отрисованных строк = totalSize. */
const padTop = computed<number>(() => {
  const first = props.virtualRows[0];
  return first ? Math.max(0, first.start - props.scrollMargin) : 0;
});
const padBottom = computed<number>(() => {
  const arr = props.virtualRows;
  const last = arr[arr.length - 1];
  if (!last) return 0;
  return Math.max(0, props.totalSize - (last.end - props.scrollMargin));
});

/* Пара «виртуальная строка + её данные». После резкой смены данных виртуализатор
   может кратко вернуть индекс за пределами rows — такие пары отсекаем, чтобы шаблон
   не читал rows[index] как undefined. */
const windowRows = computed(() => props.virtualRows
  .map((vr) => ({ vr, row: props.rows[vr.index] }))
  .filter((x): x is { vr: VirtualItem; row: LiteRow } => !!x.row));

/* Регистрируем корневой <tr> строки в виртуализаторе для замера высоты. Функциональный
   ref на компонент отдаёт его инстанс — берём у него экспонированный rootEl (или $el). */
function registerRow(comp: any) {
  const el: HTMLElement | null = comp ? (comp.rootEl ?? comp.$el ?? null) : null;
  props.measureElement?.(el);
}



const bodyScrollEl = ref<HTMLElement | null>(null);
const tableEl = ref<HTMLTableElement | null>(null);

__expose({
  get bodyScrollEl() { return bodyScrollEl.value; },
  get tableEl() { return tableEl.value; },
});

const tableStyle = computed<CSSProperties>(() => {
  if (props.widthsLocked && (props.lockedWidthPx ?? 0) > 0) {
    // идёт таскание границы: держим auto-раскладку, чтобы браузер не давал ужать колонку
    // уже содержимого (текст переносится, а не обрезается), но ширину таблицы фиксируем в
    // px — место перераспределяется только между двумя колонками. Ширины колонок задаёт
    // dragLiveWidths (см. colStyle). Без этого ре-рендер виртуализатора вернул бы fixed.
    if (isColumnDragLayoutAuto.value) {
      return { tableLayout: "auto", width: `${props.lockedWidthPx}px` };
    }
    // все ширины известны: держим их жёстко, браузер ничего не пересчитывает сам
    return { tableLayout: "fixed", width: `${props.lockedWidthPx}px` };
  }
  // ширины ещё не зафиксированы: пусть браузер сам разложит колонки по содержимому,
  // но не уже заданной доли окна
  return {
    tableLayout: "auto",
    width: "min-content",
    minWidth: `${props.effectiveMultiplier * 100}%`,
  };
});

function colStyle(col: LiteColumn): CSSProperties | undefined {
  // во время таскания границы при виртуализации живая ширина имеет приоритет: иначе
  // ре-рендер тела вернул бы колонку к исходной ширине (см. dragLiveWidths)
  const live = props.dragLiveWidths;
  if (live && live[col.id] > 0) return { width: `${live[col.id]}px` };
  const frozen = props.frozenColWidths;
  if (frozen && frozen[col.id] > 0) return { width: `${frozen[col.id]}px` };
  if (col.isMultiedit) return { width: "55px" };
  const w = props.userWidths[col.id];
  return w > 0 ? { width: `${w}px` } : undefined;
}

const bodyScrollClass = computed(() => ({
  "qtl-body-scroll--dim": props.contentMode === "dim",
  "qtl-body-scroll--hide": props.contentMode === "hide",
  "qtl-body-scroll--page": props.headerMode === "page",
  "qtl-body-scroll--locked": !!props.scrollLocked,
}));

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "bodyScrollEl",
    ref: bodyScrollEl,
    class: _normalizeClass(["qtl-body-scroll", bodyScrollClass.value])
  }, [
    (_ctx.formulaResults && _ctx.formulaResults.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.formulaResults, (fr, i) => {
            return (_openBlock(), _createElementBlock("div", {
              key: i,
              class: "qtl-formulas__item"
            }, _toDisplayString(fr.Name) + ": " + _toDisplayString(_unref(formatNumber)(fr.Value)), 1))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("table", {
      ref_key: "tableEl",
      ref: tableEl,
      class: "qtl-table qtl-table--body",
      style: _normalizeStyle(tableStyle.value)
    }, [
      _createElementVNode("colgroup", null, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.columns, (col) => {
          return (_openBlock(), _createElementBlock("col", {
            key: col.id,
            "data-col-id": col.id,
            style: _normalizeStyle(colStyle(col))
          }, null, 12, _hoisted_2))
        }), 128))
      ]),
      _createVNode(QTLiteHeader, {
        columns: _ctx.columns,
        "header-mode": _ctx.headerMode,
        "header-checkbox": _ctx.headerCheckbox,
        WA: _ctx.WA,
        "multiedit-allowed": _ctx.multieditAllowed,
        "order-by-pinned": _ctx.orderByPinned,
        "show-global-pinned-sort": _ctx.showGlobalPinnedSort,
        blocked: !!_ctx.scrollLocked,
        onHeaderCheckboxChange: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('headerCheckboxChange', $event))),
        onSort: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('sort', $event))),
        onMultieditClick: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('multieditClick', $event))),
        onTogglePinnedOrder: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('togglePinnedOrder')))
      }, null, 8, ["columns", "header-mode", "header-checkbox", "WA", "multiedit-allowed", "order-by-pinned", "show-global-pinned-sort", "blocked"]),
      (!useVirtual.value)
        ? (_openBlock(), _createElementBlock("tbody", _hoisted_3, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(renderedRows.value, (row, idx) => {
              return (_openBlock(), _createBlock(QTLiteRow, {
                key: `${row.entityId ?? 'row'}:${idx}`,
                row: row,
                "row-index": idx,
                "row-number-base": _ctx.rowNumberBase,
                columns: _ctx.columns,
                "width-stamp": _ctx.rowHeightAlignStamp,
                "src-element": _ctx.srcElement,
                WA: _ctx.WA,
                "is-checked": _ctx.isRowChecked(row.entityId),
                onToggleCheckbox: _cache[4] || (_cache[4] = ($event: any) => (_ctx.$emit('toggleCheckbox', $event))),
                onChangeHoverRecord: _cache[5] || (_cache[5] = ($event: any) => (_ctx.$emit('changeHoverRecord', $event))),
                onChangeHeights: _cache[6] || (_cache[6] = ($event: any) => (_ctx.$emit('changeHeights', $event))),
                onRefreshNeeded: _cache[7] || (_cache[7] = ($event: any) => (_ctx.$emit('refreshNeeded'))),
                onUpdatePerson: _cache[8] || (_cache[8] = ($event: any) => (_ctx.$emit('updatePerson', $event))),
                onUpdateSelection: _cache[9] || (_cache[9] = ($event: any) => (_ctx.$emit('updateSelection', $event))),
                onCheckBoxClick: _cache[10] || (_cache[10] = ($event: any) => (_ctx.$emit('checkBoxClick'))),
                onSourceItemsRequested: _cache[11] || (_cache[11] = ($event: any) => (_ctx.$emit('sourceItemsRequested', $event)))
              }, null, 8, ["row", "row-index", "row-number-base", "columns", "width-stamp", "src-element", "WA", "is-checked"]))
            }), 128))
          ]))
        : (_openBlock(), _createElementBlock("tbody", _hoisted_4, [
            (padTop.value > 0)
              ? (_openBlock(), _createElementBlock("tr", _hoisted_5, [
                  _createElementVNode("td", {
                    colspan: colCount.value,
                    style: _normalizeStyle({ height: padTop.value + 'px' })
                  }, null, 12, _hoisted_6)
                ]))
              : _createCommentVNode("", true),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(windowRows.value, (w) => {
              return (_openBlock(), _createBlock(QTLiteRow, {
                key: w.vr.key,
                ref_for: true,
                ref: registerRow,
                "data-index": w.vr.index,
                row: w.row,
                "row-index": w.vr.index,
                "row-number-base": _ctx.rowNumberBase,
                columns: _ctx.columns,
                "width-stamp": _ctx.rowHeightAlignStamp,
                "src-element": _ctx.srcElement,
                WA: _ctx.WA,
                "is-checked": _ctx.isRowChecked(w.row.entityId),
                onToggleCheckbox: _cache[12] || (_cache[12] = ($event: any) => (_ctx.$emit('toggleCheckbox', $event))),
                onChangeHoverRecord: _cache[13] || (_cache[13] = ($event: any) => (_ctx.$emit('changeHoverRecord', $event))),
                onChangeHeights: _cache[14] || (_cache[14] = ($event: any) => (_ctx.$emit('changeHeights', $event))),
                onRefreshNeeded: _cache[15] || (_cache[15] = ($event: any) => (_ctx.$emit('refreshNeeded'))),
                onUpdatePerson: _cache[16] || (_cache[16] = ($event: any) => (_ctx.$emit('updatePerson', $event))),
                onUpdateSelection: _cache[17] || (_cache[17] = ($event: any) => (_ctx.$emit('updateSelection', $event))),
                onCheckBoxClick: _cache[18] || (_cache[18] = ($event: any) => (_ctx.$emit('checkBoxClick'))),
                onSourceItemsRequested: _cache[19] || (_cache[19] = ($event: any) => (_ctx.$emit('sourceItemsRequested', $event)))
              }, null, 8, ["data-index", "row", "row-index", "row-number-base", "columns", "width-stamp", "src-element", "WA", "is-checked"]))
            }), 128)),
            (padBottom.value > 0)
              ? (_openBlock(), _createElementBlock("tr", _hoisted_7, [
                  _createElementVNode("td", {
                    colspan: colCount.value,
                    style: _normalizeStyle({ height: padBottom.value + 'px' })
                  }, null, 12, _hoisted_8)
                ]))
              : _createCommentVNode("", true)
          ]))
    ], 4),
    (!_ctx.rows.length && !_ctx.loading)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          _renderSlot(_ctx.$slots, "empty", {}, () => [
            _createTextVNode(_toDisplayString(_ctx.emptyMessage), 1)
          ])
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})